import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { levelThumbnail } from "../components/levelThumbnails";
import { useLocalProfile } from "../local/useLocalProfile";
import { WORD_PACKS, type WordPack } from "../data/word-packs";
import { listCustomPacks } from "../local/customPacks";
import { useSearchParams } from "react-router-dom";
import { TEACHING_LEVELS } from "../data/teaching-levels";
import { QUIZ_STYLES, type QuizStyleId } from "../data/quizStyles";
import { isLessonFinished } from "../local/profile";
import { SEED_LESSONS } from "../data/seed-lessons";
import { GameImage } from "../game/ui/GameImage";

function thumbIcon(courseMode: string): string {
  switch (courseMode) {
    case "hit-parade":     return "🎯";
    case "door-dash":      return "🚪";
    case "rolling-tubes":  return "🪓";
    case "circle-spinner": return "🔄";
    case "double-spinner": return "🌀";
    case "maze-rush":      return "🧩";
    case "space-ship":     return "🚀";
    case "ice-crack":      return "❄️";
    case "supermarket-sweep": return "🛒";
    case "block-climb":       return "🧱";
    case "river-boat":        return "🚤";
    case "train-roof":        return "🚂";
    default:               return "🏃";
  }
}

/**
 * Level thumbnail. If `/images/levels/level-{index}.jpg` (or .png) exists
 * in /public, we render that real screenshot. Otherwise we fall back to
 * the emoji icon so the picker keeps looking right with no images on disk.
 *
 * To replace any level's thumbnail with a real in-game screenshot:
 *   1. Take a screenshot at 480×320 (or any ~16:10 ratio).
 *   2. Save it as `apps/web/public/images/levels/level-3.jpg`
 *      (use the level's `index` from teaching-levels.ts).
 *   3. Reload — the picker will pick it up automatically.
 */
function LevelThumbnail({ courseMode, eager }: { index: number; courseMode: string; eager: boolean }) {
  // Render a real 3D snapshot of the level — but only for cards near the
  // centre of the carousel (eager), so we don't build all 11 courses at once.
  // Each render is cached after the first build.
  const [url, setUrl] = useState<string>(() => levelThumbnail(courseMode) || "");
  useEffect(() => {
    if (url || !eager) return;
    let cancelled = false;
    const id = requestAnimationFrame(() => {
      const u = levelThumbnail(courseMode);
      if (!cancelled && u) setUrl(u);
    });
    return () => { cancelled = true; cancelAnimationFrame(id); };
  }, [courseMode, eager, url]);
  if (url) {
    return <img src={url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "inherit" }} />;
  }
  return <span className="picker2-clevel-emoji">{thumbIcon(courseMode)}</span>;
}

/**
 * Combined picker — both the Word Pack chooser and the Level chooser on
 * the same screen, matching the reference design. Picking a pack updates
 * the words pill row and the level grid below it.
 */
export function PackLevelPicker() {
  const nav = useNavigate();
  const { profile } = useLocalProfile();
  const [params] = useSearchParams();
  const allPacks = useMemo<WordPack[]>(
    () => [...WORD_PACKS, ...listCustomPacks()],
    [],
  );
  const initialPack = (() => {
    const wantId = params.get("pack");
    return (wantId && allPacks.find((p) => p.id === wantId)) || allPacks[0] || null;
  })();
  const [chosenPack, setChosenPack] = useState<WordPack | null>(initialPack);
  const [chosenLevel, setChosenLevel] = useState<number>(1);
  const [centerIdx, setCenterIdx] = useState(0);
  const [packIdx, setPackIdx] = useState(() => Math.max(0, allPacks.findIndex((p) => p.id === initialPack?.id)));
  const [packPage, setPackPage] = useState(0);
  const [levelPage, setLevelPage] = useState(0);
  const [quizStyle, setQuizStyle] = useState<QuizStyleId>("default");

  // Swipeable scroll-snap rows show every pack/level inline; no pagination.
  void packPage; void levelPage; void setPackPage; void setLevelPage;
  const visiblePacks = allPacks;
  const showCustomTile = true;
  const visibleLevels = TEACHING_LEVELS;

  // Keep the chosen level in sync with the carousel's centred card.
  useEffect(() => {
    const lvl = visibleLevels[centerIdx];
    if (lvl) setChosenLevel(lvl.index);
  }, [centerIdx, visibleLevels]);

  // Keep the chosen pack in sync with the pack carousel's centred card.
  useEffect(() => {
    const p = visiblePacks[packIdx];
    if (p) { setChosenPack(p); setChosenLevel(1); setCenterIdx(0); }
  }, [packIdx, visiblePacks]);

  const levelsCompleted = useMemo(
    () => (profile ? SEED_LESSONS.filter((l) => isLessonFinished(profile, l.id)).length : 0),
    [profile],
  );

  // All hooks are above — safe to bail now if the profile isn't ready yet
  // (keeps hook order stable → avoids React #310 on a cold load of /play).
  if (!profile) {
    nav("/", { replace: true });
    return null;
  }

  const quizQuery = quizStyle !== "default" ? `?quiz=${quizStyle}` : "";
  const playLevel = () => {
    if (!chosenPack) return;
    nav(`/play/pack/${chosenPack.id}/level/${chosenLevel}${quizQuery}`);
  };
  const playRandom = () => {
    if (!chosenPack) return;
    const idx = Math.max(1, Math.floor(Math.random() * TEACHING_LEVELS.length) + 1);
    nav(`/play/pack/${chosenPack.id}/level/${idx}${quizQuery}`);
  };

  return (
    <div className="picker2-page">
      <header className="picker2-top">
        <button className="picker2-back" onClick={() => nav("/")} aria-label="Back">←</button>
        <h1 className="picker2-title">
          <span className="picker2-star" aria-hidden>⭐</span> WORD RUNNER <span className="picker2-star" aria-hidden>⭐</span>
        </h1>
        <div className="picker2-progress">
          <span className="picker2-progress-star" aria-hidden>⭐</span>
          <div>
            <strong><em>{levelsCompleted}</em> / 80</strong>
            <small>levels completed</small>
          </div>
        </div>
      </header>

      {/* Step 1: Choose a Word Pack — 3D carousel */}
      <section className="picker2-section picker2-carousel-section">
        <header className="picker2-carousel-head">
          <h2>✦ Choose a Word Pack ✦</h2>
          <p>Pick a vocabulary set to learn!</p>
        </header>
        <div className="picker2-carousel">
          <button
            className="picker2-carousel-arrow"
            onClick={() => setPackIdx((i) => Math.max(0, i - 1))}
            disabled={packIdx === 0}
            aria-label="Previous pack"
          >‹</button>
          <div className="picker2-carousel-viewport">
            <div
              className="picker2-carousel-track"
              style={{ transform: `translateX(calc(50% - ${(packIdx + 0.5) * 210}px))` }}
            >
              {visiblePacks.map((pack, idx) => {
                const dist = idx - packIdx;
                const ad = Math.min(2.4, Math.abs(dist));
                const active = idx === packIdx;
                const learnedCount = pack.words.filter((w) => profile.mastery[w.id]?.timesCorrect).length;
                return (
                  <button
                    key={pack.id}
                    className={`picker2-clevel picker2-cpack ${active ? "is-center" : ""}`}
                    style={{ transform: `scale(${1 - ad * 0.16})`, opacity: 1 - ad * 0.34, zIndex: 20 - Math.round(ad * 5) }}
                    onClick={() => setPackIdx(idx)}
                  >
                    <span className="picker2-clevel-art">
                      {pack.words[0] ? (
                        <GameImage src={pack.words[0].imageUrl} alt={pack.title} fallbackLabel="★" />
                      ) : "📚"}
                    </span>
                    <strong className="picker2-clevel-name">{pack.title}</strong>
                    <small className="picker2-clevel-sub">{pack.subtitle}</small>
                    <div className="picker2-pack-bar">
                      <i style={{ width: `${(learnedCount / Math.max(1, pack.words.length)) * 100}%` }} />
                    </div>
                    <span className="picker2-pack-count">{learnedCount} / {pack.words.length} words</span>
                  </button>
                );
              })}
            </div>
          </div>
          <button
            className="picker2-carousel-arrow"
            onClick={() => setPackIdx((i) => Math.min(visiblePacks.length - 1, i + 1))}
            disabled={packIdx === visiblePacks.length - 1}
            aria-label="Next pack"
          >›</button>
        </div>
        <div className="picker2-dots">
          {visiblePacks.map((pack, idx) => (
            <button
              key={pack.id}
              className={`picker2-dot ${idx === packIdx ? "is-on" : ""}`}
              onClick={() => setPackIdx(idx)}
              aria-label={pack.title}
            />
          ))}
        </div>
        {showCustomTile && (
          <div className="picker2-custom-row">
            <button className="picker2-custom-btn" onClick={() => nav("/custom-pack")}>
              <span aria-hidden>＋</span> Make your own pack
            </button>
          </div>
        )}
      </section>

      {/* Step 2: Choose a Level — 3D carousel */}
      <section className="picker2-section picker2-carousel-section">
        <header className="picker2-carousel-head">
          <h2>✦ Choose a Level ✦</h2>
          <p>Earn stars by completing levels and unlock new challenges!</p>
        </header>
        <div className="picker2-carousel">
          <button
            className="picker2-carousel-arrow"
            onClick={() => setCenterIdx((i) => Math.max(0, i - 1))}
            disabled={centerIdx === 0}
            aria-label="Previous level"
          >‹</button>
          <div className="picker2-carousel-viewport">
            <div
              className="picker2-carousel-track"
              style={{ transform: `translateX(calc(50% - ${(centerIdx + 0.5) * 210}px))` }}
            >
              {visibleLevels.map((lvl, idx) => {
                const dist = idx - centerIdx;
                const ad = Math.min(2.4, Math.abs(dist));
                const active = idx === centerIdx;
                const lessonId = chosenPack ? `pack:${chosenPack.id}::L${lvl.index}` : "";
                const finished = !!profile.progress[lessonId]?.firstFinishedAt;
                const stars = finished ? 3 : active ? 1 : 0;
                return (
                  <button
                    key={lvl.index}
                    className={`picker2-clevel ${active ? "is-center" : ""}`}
                    style={{
                      transform: `scale(${1 - ad * 0.16})`,
                      opacity: 1 - ad * 0.34,
                      zIndex: 20 - Math.round(ad * 5),
                    }}
                    onClick={() => setCenterIdx(idx)}
                  >
                    <span className="picker2-clevel-num">{lvl.index}</span>
                    <span className="picker2-clevel-art">
                      <LevelThumbnail index={lvl.index} courseMode={lvl.courseMode} eager={Math.abs(dist) <= 1} />
                    </span>
                    <strong className="picker2-clevel-name">{lvl.title}</strong>
                    <small className="picker2-clevel-sub">{lvl.subtitle}</small>
                    <span className="picker2-clevel-stars">
                      {[0, 1, 2].map((n) => (
                        <span key={n} className={n < stars ? "on" : ""}>★</span>
                      ))}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
          <button
            className="picker2-carousel-arrow"
            onClick={() => setCenterIdx((i) => Math.min(visibleLevels.length - 1, i + 1))}
            disabled={centerIdx === visibleLevels.length - 1}
            aria-label="Next level"
          >›</button>
        </div>
        <div className="picker2-dots">
          {visibleLevels.map((lvl, idx) => (
            <button
              key={lvl.index}
              className={`picker2-dot ${idx === centerIdx ? "is-on" : ""}`}
              onClick={() => setCenterIdx(idx)}
              aria-label={`Level ${lvl.index}`}
            />
          ))}
        </div>
      </section>

      {/* Words preview */}
      {chosenPack && (
        <section className="picker2-words">
          <span className="picker2-words-icon" aria-hidden>💬</span>
          <div className="picker2-words-meta">
            <strong>Words in this pack</strong>
            <small>These are the words you will learn.</small>
          </div>
          <div className="picker2-words-list">
            {chosenPack.words.map((w) => (
              <span key={w.id} className="picker2-word">
                <GameImage src={w.imageUrl} alt={w.english} fallbackLabel="★" />
                <strong>{w.english}</strong>
              </span>
            ))}
          </div>
        </section>
      )}

      {/* Step 3: Quiz style */}
      <section className="picker2-section picker2-quiz">
        <header className="picker2-carousel-head">
          <h2>✦ Quiz Style ✦</h2>
          <p>Choose how the words are tested.</p>
        </header>
        <div className="picker2-quiz-row">
          {QUIZ_STYLES.map((q) => (
            <button
              key={q.id}
              className={`picker2-quiz-chip ${quizStyle === q.id ? "is-on" : ""}`}
              onClick={() => setQuizStyle(q.id)}
            >
              <span className="picker2-quiz-icon" aria-hidden>{q.icon}</span>
              <strong>{q.label}</strong>
              <small>{q.hint}</small>
            </button>
          ))}
        </div>
      </section>

      {/* Bottom action row */}
      <footer className="picker2-actions">
        <button className="picker2-play" onClick={playLevel} disabled={!chosenPack}>
          <span aria-hidden>▶</span> Play Level {chosenLevel}
        </button>
        <button
          className="picker2-flashcards"
          onClick={() => chosenPack && nav(`/flashcards/${chosenPack.id}`)}
          disabled={!chosenPack}
        >
          <span aria-hidden>📇</span> Flashcards
        </button>
        <button className="picker2-random" onClick={playRandom} disabled={!chosenPack}>
          <span aria-hidden>🔀</span> Random Level
        </button>
      </footer>
    </div>
  );
}
